import { API, graphqlOperation } from "aws-amplify";
import { getUserInfo, CONTROL_ACTION_TYPE, getExchangeInstrumentAndAccount } from "../utils";
import * as mutations from "../graphql/mutations";
import { ACCOUNT_MANAGEMENT_MODAL_COMMAND } from "../components/admin/AccountManagementModal";

export const EL_ADMIN_ACTION = {
  START_TRADE: "START_TRADE",
  PAUSE_TRADE: "PAUSE_TRADE",
  EMPTY_POSITION: "EMPTY_POSITION",
  SET_LEVERAGE: "SET_LEVERAGE",
  SET_ACCOUNT_PERMISSION: "SET_ACCOUNT_PERMISSION",
  NEW_ACCOUNT: "NEW_ACCOUNT",
  NEW_SESSION: "NEW_SESSION",
  DELETE_ACCOUNT: "DELETE_ACCOUNT",
  UPDATE_API_KEYS: "UPDATE_API_KEYS",
  ENABLE_MODEL: "ENABLE_MODEL",
  DISABLE_MODEL: "DISABLE_MODEL",
};

const COMMAND_TO_EL_ADMIN_ACTION = {
  [ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT]: EL_ADMIN_ACTION.NEW_ACCOUNT,
  [ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT]: EL_ADMIN_ACTION.DELETE_ACCOUNT,
  [ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION]: EL_ADMIN_ACTION.NEW_SESSION,
};

export class ElAdminEventLogService {
  static async getElAdminUser() {
    const userInfo = await getUserInfo();
    return userInfo.username;
  }

  static async saveTradeActionEvent(tradeStatusDetails, username) {
    const {
      leverage,
      practicalLeverage,
      symbol,
      account,
      exchange: exchangeInstrument,
      marginBalance,
      actionType,
      configs,
      formConfigParams,
    } = tradeStatusDetails;
    /**
     * Construct description param
     * 当formConfigParams 与 tradeStatus出现不同时，以formConfigParams为准
     */
    const tradingExchangeInstrument = formConfigParams?.exchange || exchangeInstrument;
    const tradingAccount = formConfigParams?.account || account;
    const tradingPair = formConfigParams?.pair || symbol;
    let elAdminUser = username || (await ElAdminEventLogService.getElAdminUser());
    elAdminUser = ElAdminEventLogService.replaceVictorUsernameWithHoovestAgent(elAdminUser);
    const timestamp = new Date().toISOString();
    let tradingLeverage;
    let standardAction;
    if (actionType === CONTROL_ACTION_TYPE["Start Trade"]) {
      standardAction = EL_ADMIN_ACTION.START_TRADE;
      tradingLeverage = undefined; // tradingLeverage is read from exchange AFTER the starting trade command
    } else if (actionType === CONTROL_ACTION_TYPE["Close Position"]) {
      standardAction = EL_ADMIN_ACTION.EMPTY_POSITION;
      tradingLeverage = `${leverage}(${practicalLeverage})`;
    } else if (actionType === undefined) {
      standardAction = EL_ADMIN_ACTION.PAUSE_TRADE;
      tradingLeverage = `${leverage}(${practicalLeverage})`;
    }
    // Safely parse configs
    let parsedConfigs = {};
    try {
      if (typeof configs === "string") {
        parsedConfigs = JSON.parse(configs);
        while (typeof parsedConfigs === "string") {
          parsedConfigs = JSON.parse(parsedConfigs);
        }
      } else {
        parsedConfigs = configs;
      }
    } catch (e) {
      console.error("Failed to parse configs:", e);
    }

    const description = `${timestamp} - ${elAdminUser} executed trade action '${standardAction}' on account '${tradingExchangeInstrument}_${tradingAccount}' for pair '${tradingPair}' with the following details: ${JSON.stringify(
      {
        tradingLeverage,
        marginBalance,
        ...parsedConfigs,
        ...formConfigParams,
      }
    )}`;
    console.log("description", description);
    /**
     * send mutation
     */
    try {
      await API.graphql(
        graphqlOperation(mutations.createELAdminEventLog, {
          input: {
            action: standardAction,
            adminUser: elAdminUser,
            timestamp: timestamp,
            balance: marginBalance,
            leverage: tradingLeverage,
            symbol: tradingPair,
            tradeAccount: tradingAccount,
            exchangeInstrument: tradingExchangeInstrument,
            description,
          },
        })
      );
    } catch (error) {
      console.error(`saveTradeActionEvent ${standardAction} failed`, error);
    }
  }

  static async saveSetLeverageEvent(targetLeverage, actionDetails) {
    const { leverage: oldLeverage, leverageScaleFactor, symbol, account, exchange: exchangeInstrument, marginBalance } = actionDetails;
    /**
     * Construct description param
     */
    let elAdminUser = await ElAdminEventLogService.getElAdminUser();
    elAdminUser = ElAdminEventLogService.replaceVictorUsernameWithHoovestAgent(elAdminUser);
    const timestamp = new Date().toISOString();
    const description = `${timestamp} - ${elAdminUser} updated levearge on account '${exchangeInstrument}_${account}' for symbol '${symbol}' from ${oldLeverage} to ${targetLeverage}`;
    /**
     * send mutation
     */
    try {
      await API.graphql(
        graphqlOperation(mutations.createELAdminEventLog, {
          input: {
            action: EL_ADMIN_ACTION.SET_LEVERAGE,
            adminUser: elAdminUser,
            timestamp: timestamp,
            balance: marginBalance,
            leverage: `${targetLeverage}(${targetLeverage * leverageScaleFactor})`,
            symbol: symbol,
            tradeAccount: account,
            exchangeInstrument,
            description,
          },
        })
      );
    } catch (error) {
      console.error("saveSetLeverageEvent failed", error);
    }
  }

  static replaceVictorUsernameWithHoovestAgent(username) {
    if (username === "victor.hogrefe") {
      return "HoovestAgent";
    }
    return username;
  }

  static async saveAccountManagementEvent(command, session, exchangeInstrument) {
    const elAdminUser = await ElAdminEventLogService.getElAdminUser();
    const timestamp = new Date().toISOString();
    let description;
    const action = COMMAND_TO_EL_ADMIN_ACTION[command];
    if (!action) {
      console.error("Invalid command provided:", command);
      return;
    }
    switch (action) {
      case EL_ADMIN_ACTION.NEW_ACCOUNT:
        description = `${timestamp} - ${elAdminUser} created trade account '${exchangeInstrument}_${session}'`;
        break;
      case EL_ADMIN_ACTION.NEW_SESSION:
        description = `${timestamp} - ${elAdminUser} created trade session '${exchangeInstrument}_${session}'`;
        break;
      case EL_ADMIN_ACTION.DELETE_ACCOUNT:
        description = `${timestamp} - ${elAdminUser} deleted trade session '${exchangeInstrument}_${session}'`;
        break;
      default:
        console.log("Invalid action:", action);
        return;
    }
    try {
      await API.graphql(
        graphqlOperation(mutations.createELAdminEventLog, {
          input: {
            action,
            adminUser: elAdminUser,
            timestamp,
            tradeAccount: session,
            exchangeInstrument,
            description,
          },
        })
      );
    } catch (error) {
      console.log(`${action} event failed`, error);
    }
  }

  static async saveUpdateApiKeysEvent(apiKeyAccount) {
    const elAdminUser = await ElAdminEventLogService.getElAdminUser();
    const timestamp = new Date().toISOString();
    const description = `${timestamp} - ${elAdminUser} updated API keys for account '${apiKeyAccount}'`;

    const { exchangeInstrument, tradeAccount } = getExchangeInstrumentAndAccount(apiKeyAccount);
    try {
      await API.graphql(
        graphqlOperation(mutations.createELAdminEventLog, {
          input: {
            action: EL_ADMIN_ACTION.UPDATE_API_KEYS,
            adminUser: elAdminUser,
            timestamp: timestamp,
            tradeAccount: tradeAccount,
            exchangeInstrument: exchangeInstrument,
            description,
          },
        })
      );
    } catch (error) {
      console.log("saveUpdateApiKeysEvent failed", error);
    }
  }

  static async saveEnableDisableModelEvent(model1, model2, selectedSymbol, action) {
    const elAdminUser = await ElAdminEventLogService.getElAdminUser();
    const timestamp = new Date().toISOString();
    let description;
    let eventAction;
    if (action === "enable") {
      eventAction = EL_ADMIN_ACTION.ENABLE_MODEL;
      description = `${timestamp} - ${elAdminUser} enabled models '${model1}' and '${model2}`;
    } else if (action === "disable") {
      eventAction = EL_ADMIN_ACTION.DISABLE_MODEL;
      description = `${timestamp} - ${elAdminUser} disabled models '${model1}' and '${model2}'`;
    } else {
      console.error("Invalid action provided for enabling/disabling models:", action);
      return;
    }
    try {
      await API.graphql(
        graphqlOperation(mutations.createELAdminEventLog, {
          input: {
            action: eventAction,
            adminUser: elAdminUser,
            timestamp,
            symbol: selectedSymbol,
            description,
          },
        })
      );
    } catch (error) {
      console.error(`saveEnableDisableModelEvent ${eventAction} failed`, error);
      throw new Error(`Failed to log the ${eventAction} event due to an API error.`);
    }
  }
}
